import { decodeHtmlEntities } from "@/utils/helper";
import type { SiteAllergenTabItem } from "@/components/ActiveProjects/AllergensAndSensitivities/types";
import {
  composeSkippedStepKeys,
  getReviewPanelSkippedStepKeysFromSource,
} from "@/utils/statusBarWorkflowStepUtils";

/**
 * Selectors for Active Projects product info (`/productInfo/details`).
 * Nested shape: `vendorDetails[groupIndex]` is a vendor group; assessments live on
 * `vendorList[versionIndex]` (e.g. `assessmentId`, `vendorIngredientDetails`).
 */

/** Normalized tokens from `GET /productInfo/details` vendor assessment `workflowStatuses` / `workflowStatus`. */
const PRODUCT_INFO_VERSION_REQUEST_PIPELINE = new Set([
  "version_requested",
  "version_cloning",
  "version_clonning",
  "version_approved",
]);

/**
 * Collects normalized workflow tokens (lowercase, spaces → underscores) from scalar, array, or nested API shapes.
 * Mirrors listing-side parsing so we do not miss earlier array entries when the last item is a different status.
 */
function collectNormalizedWorkflowStatusTokens(raw: unknown): string[] {
  if (raw == null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw.flatMap((item) => collectNormalizedWorkflowStatusTokens(item));
  }
  if (typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const nested =
      o.status ?? o.state ?? o.workflowStatus ?? o.name ?? o.label ?? o.value ?? o.code;
    if (nested !== undefined && nested !== raw) {
      return collectNormalizedWorkflowStatusTokens(nested);
    }
    return [];
  }
  if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
    const s = String(raw).trim();
    if (!s) {
      return [];
    }
    return [s.toLowerCase().replace(/\s+/g, "_")];
  }
  return [];
}

export function getActiveAssessment(
  product: Record<string, unknown> | null | undefined,
  groupIndex: number,
  versionIndex = 0
): Record<string, unknown> | null {
  const vds = product?.vendorDetails;
  if (!Array.isArray(vds) || groupIndex < 0 || groupIndex >= vds.length) {
    return null;
  }
  const group = vds[groupIndex];
  if (group == null || typeof group !== "object") {
    return null;
  }
  const g = group as Record<string, unknown>;
  const list = g.vendorList;
  if (!Array.isArray(list) || list.length === 0) {
    return g;
  }
  const safeIdx = Math.min(Math.max(versionIndex, 0), list.length - 1);
  const row = list[safeIdx];
  if (row == null || typeof row !== "object") {
    return g;
  }
  const { vendorList: _omitList, ...groupWithoutList } = g;
  return { ...groupWithoutList, ...(row as Record<string, unknown>) };
}

/**
 * True when the merged vendor assessment for the tab includes any version-request pipeline status
 * from product info (`workflowStatuses` preferred, `workflowStatus` fallback).
 */
export function isProductInfoVersionRequestInProgressForVendorTab(
  product: unknown,
  vendorGroupIndex: number,
  versionIndex = 0,
): boolean {
  const merged = getActiveAssessment(
    product as Record<string, unknown> | null | undefined,
    vendorGroupIndex,
    versionIndex,
  );
  if (merged == null) {
    return false;
  }
  const raw = merged.workflowStatuses ?? merged.workflowStatus;
  const tokens = collectNormalizedWorkflowStatusTokens(raw);
  return tokens.some((t) => PRODUCT_INFO_VERSION_REQUEST_PIPELINE.has(t));
}

/** `vendorIngredientDetails.productSpecification` for the merged assessment at (groupIndex, versionIndex). */
export function getSpecificationsForVendorGroup(
  product: unknown,
  groupIndex: number,
  versionIndex = 0
): unknown[] {
  const merged = getActiveAssessment(product as Record<string, unknown>, groupIndex, versionIndex);
  const vid = merged?.vendorIngredientDetails as { productSpecification?: unknown[] } | undefined;
  return Array.isArray(vid?.productSpecification) ? vid.productSpecification : [];
}

/** `assessmentId` on the merged vendor assessment (`vendorList[versionIndex]`). */
export function getVendorAssessmentIdForTab(
  product: unknown,
  groupIndex: number,
  versionIndex = 0
): string | number | undefined {
  const merged = getActiveAssessment(
    product as Record<string, unknown> | null | undefined,
    groupIndex,
    versionIndex
  ) as { assessmentId?: string | number; vendorAssessmentId?: string | number } | null | undefined;
  return merged?.assessmentId ?? merged?.vendorAssessmentId;
}

/** Raw `vendorDetails[groupIndex].vendorList` (version rows), or empty. */
export function getVendorVersionList(product: unknown, groupIndex: number): unknown[] {
  const vds = (product as Record<string, unknown> | null | undefined)?.vendorDetails;
  if (!Array.isArray(vds) || groupIndex < 0 || groupIndex >= vds.length) {
    return [];
  }
  const group = vds[groupIndex] as Record<string, unknown> | undefined;
  if (group == null || typeof group !== "object") {
    return [];
  }
  const list = group.vendorList;
  return Array.isArray(list) ? list : [];
}

/** One row in the version dropdown; `versionIndex` is always the real `vendorList` index from the API. */
export type VendorVersionDropdownEntry = { versionIndex: number; row: unknown };

/**
 * Entries for the version dropdown: same rows as {@link getVendorVersionList}, listed **last API row first**
 * (descending by array index). Use `versionIndex` for `MenuItem` value / `activeVendorVersionIndex` — unchanged from BE order.
 */
export function getVendorVersionListForDropdownDescending(
  product: unknown,
  groupIndex: number
): VendorVersionDropdownEntry[] {
  const list = getVendorVersionList(product, groupIndex);
  return list.map((row, versionIndex) => ({ versionIndex, row })).slice().reverse();
}

/**
 * Like {@link getVendorAssessmentIdForTab}, but when the merged assessment row has no id
 * (e.g. strip-only vendor shapes), read `assessmentId` / `vendorAssessmentId` from the raw
 * `vendorList[versionIndex]` row — same source as the version dropdown.
 */
export function getVendorAssessmentIdForTabWithRowFallback(
  product: unknown,
  groupIndex: number,
  versionIndex = 0
): string {
  const mergedId = getVendorAssessmentIdForTab(product, groupIndex, versionIndex);
  if (mergedId != null && String(mergedId).trim() !== "") {
    return String(mergedId).trim();
  }
  const rows = getVendorVersionList(product, groupIndex);
  const row = rows[versionIndex] as
    | { assessmentId?: string | number; vendorAssessmentId?: string | number }
    | undefined;
  if (row == null) return "";
  const id = row.assessmentId ?? row.vendorAssessmentId;
  return id != null && String(id).trim() !== "" ? String(id).trim() : "";
}

export type VendorSelectionResult = {
  groupIndex: number;
  versionIndex: number;
  merged: Record<string, unknown> | null;
};

/** Scan all vendor groups/versions for a matching `assessmentId`. */
export function findVendorSelectionByAssessmentId(
  product: unknown,
  assessmentId: string | number | null | undefined
): VendorSelectionResult | null {
  if (assessmentId == null || String(assessmentId).trim() === "") {
    return null;
  }
  const rec = product as Record<string, unknown> | null | undefined;
  const vds = rec?.vendorDetails;
  if (!Array.isArray(vds)) {
    return null;
  }
  for (let groupIndex = 0; groupIndex < vds.length; groupIndex += 1) {
    const versionIndex = findVersionIndexByAssessmentId(product, groupIndex, assessmentId);
    if (versionIndex >= 0) {
      return {
        groupIndex,
        versionIndex,
        merged: getActiveAssessment(rec, groupIndex, versionIndex),
      };
    }
  }
  return null;
}

export type ResolveVendorSelectionFromUrlParams = {
  vendorId?: string | number | null;
  vendorAssessmentId?: string | number | null;
};

/**
 * Resolve vendor group + version from URL query params.
 * `vendorAssessmentId` is authoritative when present; otherwise use `vendorId` at version 0.
 */
export function resolveVendorSelectionFromUrl(
  product: unknown,
  params: ResolveVendorSelectionFromUrlParams
): VendorSelectionResult | null {
  const rec = product as Record<string, unknown> | null | undefined;
  const vds = rec?.vendorDetails;
  if (!Array.isArray(vds) || vds.length === 0) {
    return null;
  }

  const assessmentId = params.vendorAssessmentId;
  if (assessmentId != null && String(assessmentId).trim() !== "") {
    const byAssessment = findVendorSelectionByAssessmentId(product, assessmentId);
    if (byAssessment) {
      return byAssessment;
    }
  }

  const vendorId = params.vendorId;
  if (vendorId == null || String(vendorId).trim() === "") {
    return {
      groupIndex: 0,
      versionIndex: 0,
      merged: getActiveAssessment(rec, 0, 0),
    };
  }

  const want = String(vendorId).trim();
  for (let groupIndex = 0; groupIndex < vds.length; groupIndex += 1) {
    const group = vds[groupIndex] as Record<string, unknown> | undefined;
    const gid = String(group?.vendorId ?? group?.id ?? "").trim();
    if (gid === want) {
      return {
        groupIndex,
        versionIndex: 0,
        merged: getActiveAssessment(rec, groupIndex, 0),
      };
    }
  }

  return null;
}

/** Index in `vendorList` whose `assessmentId` or `vendorAssessmentId` matches, or -1. */
export function findVersionIndexByAssessmentId(
  product: unknown,
  groupIndex: number,
  assessmentId: string | number | null | undefined
): number {
  if (assessmentId == null || String(assessmentId).trim() === "") {
    return -1;
  }
  const want = String(assessmentId).trim();
  const list = getVendorVersionList(product, groupIndex);
  for (let i = 0; i < list.length; i += 1) {
    const row = list[i] as { assessmentId?: string | number; vendorAssessmentId?: string | number } | undefined;
    if (row == null) continue;
    const rowKey = String(row.assessmentId ?? row.vendorAssessmentId ?? "").trim();
    if (rowKey === want) {
      return i;
    }
  }
  return -1;
}

const SKIPPED_STEP_BY_API_FIELD = {
  skippedLegalReview: "UNDER_LEGAL_REVIEW",
  skippedArtwork: "ARTWORK_IN_DESIGN",
} as const;

/** API boolean flags (`true`, `1`, `"true"`, `"1"`). */
export function isApiTruthy(value: unknown): boolean {
  if (value === true) {
    return true;
  }
  if (value === 1) {
    return true;
  }
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "true" || v === "1";
  }
  return false;
}

/**
 * Status bar `label_value` keys for workflow steps skipped on a merged vendor assessment row.
 * Reads `skippedLegalReview` / `skippedArtwork` and `proceedWithReviewPanel` from the assessment object.
 */
export function getSkippedStatusStepKeysFromAssessment(
  assessment: Record<string, unknown> | null | undefined
): string[] {
  if (assessment == null) {
    return [];
  }

  const legalAndArtworkSkips = (
    Object.entries(SKIPPED_STEP_BY_API_FIELD) as [
      keyof typeof SKIPPED_STEP_BY_API_FIELD,
      string,
    ][]
  )
    .filter(([apiKey]) => isApiTruthy(assessment[apiKey]))
    .map(([, stepKey]) => stepKey);

  return composeSkippedStepKeys(
    legalAndArtworkSkips,
    getReviewPanelSkippedStepKeysFromSource(assessment)
  );
}

/**
 * Status bar `label_value` keys for workflow steps skipped on the active vendor version.
 * Reads `skippedLegalReview`, `skippedArtwork`, and `proceedWithReviewPanel` from
 * `vendorList[versionIndex]` via {@link getActiveAssessment}.
 */
export function getSkippedStatusStepKeys(
  product: unknown,
  vendorGroupIndex: number,
  versionIndex = 0
): string[] {
  const assessment = getActiveAssessment(
    product as Record<string, unknown> | null | undefined,
    vendorGroupIndex,
    versionIndex
  );
  return getSkippedStatusStepKeysFromAssessment(assessment);
}

/**
 * Version dropdown label: prefer API `versionName`; if missing/blank use `V${index + 1}` (index is 0-based row index).
 */
/** `siteAllergen` rows on the merged vendor assessment from GET /productInfo/details. */
export type ProductInfoSiteAllergenTab = SiteAllergenTabItem;

export function getSiteAllergenTabsFromProductInfo(
  product: unknown,
  groupIndex: number,
  versionIndex = 0
): SiteAllergenTabItem[] {
  const merged = getActiveAssessment(
    product as Record<string, unknown> | null | undefined,
    groupIndex,
    versionIndex
  );
  const tabs = merged?.siteAllergen;
  if (!Array.isArray(tabs)) {
    return [];
  }
  return tabs.filter(
    (row): row is SiteAllergenTabItem =>
      row != null && typeof row === "object" && (row as SiteAllergenTabItem).id != null
  );
}

export function getSiteAllergenTabsForAssessmentId(
  product: unknown,
  assessmentId: string | number | null | undefined
): SiteAllergenTabItem[] {
  const selection = findVendorSelectionByAssessmentId(product, assessmentId);
  if (!selection) {
    return [];
  }
  return getSiteAllergenTabsFromProductInfo(
    product,
    selection.groupIndex,
    selection.versionIndex
  );
}

/** Merge product-info siteAllergen metadata (e.g. allergensDeclarationVersion) into section rows. */
export function enrichSiteSectionsWithProductInfo<T extends { id?: string | number }>(
  sections: T[],
  product: unknown,
  assessmentId: string | number | null | undefined
): Array<T & { allergensDeclarationVersion?: string }> {
  const tabs = getSiteAllergenTabsForAssessmentId(product, assessmentId);
  if (tabs.length === 0) {
    return sections;
  }
  const byId = new Map(tabs.map((tab) => [String(tab.id), tab]));
  return sections.map((section) => {
    if (section.id == null) {
      return section;
    }
    const info = byId.get(String(section.id));
    if (!info?.allergensDeclarationVersion) {
      return section;
    }
    return {
      ...section,
      allergensDeclarationVersion: info.allergensDeclarationVersion,
    };
  });
}

function patchSiteAllergenTabsOnProductRecord(args: {
  record: unknown;
  groupIndex: number;
  versionIndex: number;
  mutate: (tabs: SiteAllergenTabItem[]) => SiteAllergenTabItem[];
}): unknown {
  const { record, groupIndex, versionIndex, mutate } = args;
  if (!record || typeof record !== "object") {
    return record;
  }
  const rec = record as Record<string, unknown>;
  const vds = rec.vendorDetails;
  if (!Array.isArray(vds) || vds.length === 0) {
    return record;
  }

  const gi = Math.min(Math.max(groupIndex, 0), vds.length - 1);
  const nextRecord = { ...rec, vendorDetails: [...vds] };
  const group = nextRecord.vendorDetails[gi];
  if (!group || typeof group !== "object") {
    return record;
  }

  const nextGroup: Record<string, unknown> = { ...(group as Record<string, unknown>) };
  nextRecord.vendorDetails[gi] = nextGroup;

  const list = nextGroup.vendorList;
  if (Array.isArray(list) && list.length > 0) {
    const vi = Math.min(Math.max(versionIndex, 0), list.length - 1);
    const nextList = [...list];
    nextGroup.vendorList = nextList;
    const row = nextList[vi];
    if (!row || typeof row !== "object") {
      return record;
    }
    const nextRow: Record<string, unknown> = { ...(row as Record<string, unknown>) };
    nextList[vi] = nextRow;
    const currentTabs = getSiteAllergenTabsFromProductInfo(nextRecord, gi, vi);
    nextRow.siteAllergen = mutate(currentTabs);
    return nextRecord;
  }

  const currentTabs = Array.isArray(nextGroup.siteAllergen)
    ? (nextGroup.siteAllergen as SiteAllergenTabItem[])
    : [];
  nextGroup.siteAllergen = mutate(currentTabs);
  return nextRecord;
}

/** Append or replace a siteAllergen row in product-info context sources. */
export function upsertSiteAllergenTabInProductRecord(
  record: unknown,
  groupIndex: number,
  versionIndex: number,
  tab: SiteAllergenTabItem
): unknown {
  return patchSiteAllergenTabsOnProductRecord({
    record,
    groupIndex,
    versionIndex,
    mutate: (tabs) => {
      const id = String(tab.id);
      const without = tabs.filter((row) => String(row.id) !== id);
      return [...without, tab];
    },
  });
}

/** Remove a siteAllergen row from product-info context sources. */
export function removeSiteAllergenTabFromProductRecord(
  record: unknown,
  groupIndex: number,
  versionIndex: number,
  sectionId: string | number
): unknown {
  const want = String(sectionId);
  return patchSiteAllergenTabsOnProductRecord({
    record,
    groupIndex,
    versionIndex,
    mutate: (tabs) => tabs.filter((row) => String(row.id) !== want),
  });
}

export function getVersionOptionLabel(versionRow: unknown, fallbackIndex: number): string {
  const idx = Number(fallbackIndex);
  const safeIdx = Number.isFinite(idx) && idx >= 0 ? Math.floor(idx) : 0;
  const ordinal = `V${safeIdx + 1}`;
  if (versionRow == null || typeof versionRow !== "object") {
    return ordinal;
  }
  const r = versionRow as Record<string, unknown>;
  const raw =
    r.versionName ??
    r.VersionName ??
    r.versionLabel ??
    r.VersionLabel;
  const label =
    typeof raw === "string"
      ? raw.trim()
      : raw != null && String(raw).trim() !== ""
        ? String(raw).trim()
        : "";
  if (label !== "") {
    return decodeHtmlEntities(label);
  }
  return ordinal;
}